import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useScroll, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

export const PRACTICE_PROCESS = {
  eyebrow: "How we work",
  heading: "",
  intro: "",
  chapterClass: "min-h-[55vh] md:min-h-[72vh]",
  stages: [
    {
      n: "01",
      name: "We Investigate",
      short: "INVESTIGATE",
      descriptor: "Questions & Evidence",
      headline: "We look beyond what is changing to understand what change sets in motion.",
      body: "We explore emerging questions at the intersection of technology, human capability, organisations and society. Through interdisciplinary research, we examine patterns, tensions and relationships that are often overlooked when technological change is considered in isolation.",
      cta: { label: "Explore our research", to: "/research" },
    },
    {
      n: "02",
      name: "We Translate",
      short: "TRANSLATE",
      descriptor: "Knowledge & Understanding",
      headline: "We turn complexity into knowledge that can travel.",
      body: "Research becomes valuable when it can be understood, questioned and used beyond the context in which it was produced. We translate emerging insights into frameworks, concepts, publications, dialogues and learning experiences that make complex transformations easier to explore and act upon.",
      cta: { label: "Explore our thinking", to: "/research-dialogues" },
    },
    {
      n: "03",
      name: "We Apply",
      short: "APPLY",
      descriptor: "Practice & Experimentation",
      headline: "We bring knowledge into real systems, decisions and collaborations.",
      body: "We work with organisations, researchers and innovation ecosystems to explore how insights can inform practice. Through collaborative projects, strategic inquiry, experimentation and advisory work, we bring research into the environments where technological and human systems are actually changing.",
      cta: { label: "Work with us", to: "mailto:contact@mentheris.com" },
    },
  ],
};

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

function CtaLink({ cta }) {
  const cls = "group inline-flex items-center gap-2 mt-10 text-sm tracking-wide link-editorial";
  const inner = (
    <>
      {cta.label}
      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
    </>
  );
  return cta.to.startsWith("/") ? (
    <Link to={cta.to} data-testid="chapter-cta" className={cls}>{inner}</Link>
  ) : (
    <a href={cta.to} data-testid="chapter-cta" className={cls}>{inner}</a>
  );
}

export function ProcessCycle({ config = PRACTICE_PROCESS }) {
  const reduced = useReducedMotion();
  const trackRef = useRef(null);
  const chapterRefs = useRef([]);
  const [p, setP] = useState(0);

  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start center", "end center"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setP(Math.round(v * 1000) / 1000);
  });

  const stages = config.stages;
  const n = stages.length;
  const s = clamp(p / 0.85) * (n - 1);
  const active = Math.min(n - 1, Math.floor(s + 0.5));

  const goTo = (i) => chapterRefs.current[i]?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });

  return (
    <section data-testid="process-cycle" className="bg-white">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-6 md:pt-10 pb-20 md:pb-24">
        {(config.heading || config.intro) && (
          <Reveal>
            {config.heading && (
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight max-w-3xl" data-testid="process-heading">
                {config.heading}
              </h2>
            )}
            {config.intro && (
              <p className="mt-8 text-fog leading-relaxed max-w-2xl">{config.intro}</p>
            )}
          </Reveal>
        )}

        {/* Mobile: sticky compact indicator + visual */}
        <div className="md:hidden sticky top-16 z-30 -mx-6 px-6 mt-12 bg-bone/90 backdrop-blur-md border-y border-line py-4" data-testid="process-mobile-rail">
          <div className="flex items-center justify-between text-xs tracking-[0.2em] uppercase">
            <span className="text-fog">{stages[active].n} / 0{n}</span>
            <span className="text-ink font-medium">{stages[active].short}</span>
          </div>
          <div className="relative h-px bg-line mt-3">
            <div className="absolute left-0 top-0 h-px bg-ember transition-[width] duration-300" style={{ width: `${(s / (n - 1)) * 100}%` }} />
          </div>
        </div>

        <div ref={trackRef} className={`grid md:grid-cols-12 gap-10 md:gap-16 ${config.heading || config.intro ? "mt-8 md:mt-16" : "mt-2 md:mt-4"}`}>
          {/* Left: sticky rail */}
          <div className="hidden md:block md:col-span-5">
            <div className="sticky top-28">
              <p className="text-xs tracking-[0.25em] uppercase text-fog">{config.eyebrow}</p>

              <div className="relative mt-10" data-testid="process-rail">
                <div className="absolute left-[5px] top-3 bottom-3 w-px bg-line" aria-hidden="true" />
                <div
                  className="absolute left-[5px] top-3 w-px bg-ember origin-top"
                  style={{ height: "calc(100% - 24px)", transform: `scaleY(${s / (n - 1)})` }}
                  aria-hidden="true"
                />
                {stages.map((st, i) => (
                  <button
                    key={st.n}
                    data-testid={`rail-stage-${st.n}`}
                    onClick={() => goTo(i)}
                    className="group relative flex items-start gap-6 w-full text-left py-4 first:pt-0 last:pb-0 cursor-pointer"
                  >
                    <span
                      className={`relative z-10 mt-1.5 block w-[11px] h-[11px] rounded-full border transition-all duration-500 ${
                        active === i ? "bg-ember border-ember scale-110" : "bg-bone border-line group-hover:border-ink"
                      }`}
                      aria-hidden="true"
                    />
                    <span>
                      <span className={`block text-[11px] tracking-[0.25em] transition-colors duration-500 ${active === i ? "text-ember" : "text-fog"}`}>
                        {st.n}
                      </span>
                      <span
                        className={`block font-serif uppercase tracking-tight text-2xl lg:text-3xl mt-1 transition-colors duration-500 ${
                          active === i ? "text-ink" : "text-ink/30 group-hover:text-ink/55"
                        }`}
                      >
                        {st.name}
                      </span>
                      <span className="block text-sm text-fog mt-1">{st.descriptor}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: scrolling chapters */}
          <div className="md:col-span-7">
            {stages.map((st, i) => (
              <article
                key={st.n}
                ref={(el) => (chapterRefs.current[i] = el)}
                className={`${config.chapterClass} flex flex-col justify-center py-14 md:py-16 ${i > 0 ? "border-t border-line" : ""}`}
                data-testid={`chapter-${st.n}`}
              >
                <p className="text-[11px] tracking-[0.25em] uppercase text-fog" data-testid={`chapter-label-${st.n}`}>
                  {st.n} / {st.short}
                </p>
                <h3 className="font-serif text-2xl md:text-4xl tracking-tight leading-tight mt-6 max-w-xl">
                  {st.headline}
                </h3>
                <p className="mt-6 text-fog leading-relaxed max-w-xl">{st.body}</p>
                {st.cta && <CtaLink cta={st.cta} />}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
